import { Typography, styled } from "@mui/material";
import type { ComponentPropsWithRef } from "react";

interface PairProps extends ComponentPropsWithRef<"div"> {
  reversed?: boolean;
}
export const Pair = styled("div", {
  shouldForwardProp: (propName: keyof PairProps) => propName !== "reversed",
})<PairProps>(({ theme, reversed }) => ({
  display: "flex",
  flexDirection: reversed ? "column-reverse" : "column",
  gap: theme.spacing(2),
}));

// Assistant turn: full-width content on the panel, no bubble. (The user turn is
// the shared grey UserBubble — see chat.sx.)
export const Answer = styled("div")({
  display: "flex",
});

export const Body = styled("div")(({ theme }) => ({
  [`${Answer} &`]: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    gap: theme.spacing(2),
    // Let the column shrink below its content width so a long unbreakable
    // reference URL truncates (ReferenceItem's ellipsis) instead of forcing the
    // whole answer wider than the panel.
    minWidth: 0,
  },
  // Errors stay a tinted full-width block rather than a bubble.
  [`${Answer}.error &`]: {
    backgroundColor: theme.palette.error.background,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
  },
}));

export const UserActionButtonsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "flex-end",
}));

export const AutomaticallyDiscardedMessage = styled(Typography)(
  ({ theme }) => ({
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  }),
);

export const Text = styled(Typography)(({ theme }) => ({
  [`${Answer}.error &`]: {
    color: theme.palette.error.dark,
    fontWeight: theme.typography.fontWeightLight,
  },
})) as typeof Typography;

// A quiet status line matching the agent's event vocabulary: a teal agent
// glyph + muted caption, rather than an all-caps label on a divider.
export const ModeChangeStatement = styled("div")(({ theme }) => ({
  // Match EventLine: pin the glyph to the first line so it aligns with the top
  // of the text on multi-line messages rather than centering the whole block.
  alignItems: "flex-start",
  display: "flex",
  gap: theme.spacing(0.75),
}));

export const ModeChangeStatementText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
})) as typeof Typography;

export const ModelExpertWelcomePrompt = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

interface ModelExpertWelcomePromptLineProps extends ComponentPropsWithRef<"div"> {
  isSamplePrompt?: boolean;
}

export const ModelExpertWelcomePromptLine = styled("div", {
  shouldForwardProp: (propName: keyof ModelExpertWelcomePromptLineProps) =>
    propName !== "isSamplePrompt",
})<ModelExpertWelcomePromptLineProps>(({ theme, isSamplePrompt }) => [
  isSamplePrompt
    ? {
        color: theme.palette.text.secondary,
        fontStyle: "italic",
      }
    : {},
]);

export const References = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

export const ReferencesList = styled("dl")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  marginBlockStart: 0,
  marginBlockEnd: 0,
}));

export const ReferenceItem = styled("dt")({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});
